import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { queryClient } from './lib/queryClient'
import { routeTree } from './routeTree.gen'

import '@/components/ui/ui.css'

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // Loaders delegate caching to TanStack Query, so the router must not add a
  // second, competing staleness policy on top.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

/*
 * Registered only in a real build.
 *
 * A service worker in front of the dev server intercepts module requests and
 * fights HMR, and there is nothing to gain — installability is a property of a
 * deployed instance. `import.meta.env.PROD` is the switch Vite already has.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // An instance served over plain HTTP has no service workers at all, which
      // is a supported way to run nook. It simply is not installable there.
    })
  })
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('index.html is missing #root')

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
